import { canAddPersonToBidEvent, canAddPersonToPortal, isPersonOnBidEvent, isPersonOnPortal } from 'util/person';
import {
  canAddSupplierToBidEvent,
  canAddSupplierToPortal,
  isSupplierOnBidEvent,
  isSupplierOnPortal
} from 'util/supplier';
import { canAddVendorToBidEvent, canAddVendorToPortal, isVendorOnBidEvent, isVendorOnPortal } from 'util/vendor';

//

/**
 * "Record" Utils
 *
 * In the context of this application, a record is a general term used in search results. In general,
 * it is a "tree node" from the search result tree's, and they can represent a person, supplier, or
 * a vendor.
 */

/**
 * Can this record (person, company or vendor) be added to the current bid event
 *
 * @param {any} record search result data object (node from search result tree provider)
 * @param {readonly any[]} bidTeams array of bid teams on the current bid event
 * @returns {boolean} true if record can be added to bid event, false otherwise
 */
export function canAddRecordToBidEvent(record, bidTeams = []) {
  if (!record) {
    return false;
  }

  if (record.isPerson) {
    return canAddPersonToBidEvent(record, bidTeams);
  }

  if (record.isSupplier) {
    return canAddSupplierToBidEvent(record, bidTeams);
  }

  return canAddVendorToBidEvent(record, bidTeams);
}

/**
 * Can this record (person, company or vendor) be added to the current bid event
 *
 * @param {any} record search result data object (node from search result tree provider)
 * @param {readonly SupplierGroupType[]} supplierGroups array of supplier groups on the current portal
 * @returns {boolean} true if record can be added to portal, false otherwise
 */
export function canAddRecordToPortal(record, supplierGroups = []) {
  if (!record) {
    return false;
  }

  if (record.isPerson) {
    return canAddPersonToPortal(record, supplierGroups);
  }

  if (record.isSupplier) {
    return canAddSupplierToPortal(record, supplierGroups);
  }

  return canAddVendorToPortal(record, supplierGroups);
}

/**
 * Tells us if a search result record (person, company or vendor) is part of an existing bid
 * team
 *
 * @param {any} record search result data object (node from search result tree provider)
 * @param {readonly any[]} bidTeams array of bid teams on the current bid event
 * @returns {boolean} true if record can be added to bid event, false otherwise
 */
export function isRecordOnBidEvent(record, bidTeams = []) {
  if (!record) {
    return false;
  }

  if (record.isPerson) {
    return isPersonOnBidEvent(record, bidTeams);
  }

  if (record.isSupplier) {
    return isSupplierOnBidEvent(record, bidTeams);
  }

  return isVendorOnBidEvent(record, bidTeams);
}

/**
 * is record part of supplier groups
 *
 * @param {any} record
 * @param {readonly SupplierGroupType[]} supplierGroups
 */
export function isRecordOnPortal(record, supplierGroups = []) {
  if (!record) {
    return false;
  }

  if (record.isPerson) {
    return isPersonOnPortal(record, supplierGroups);
  }

  if (record.isSupplier) {
    return isSupplierOnPortal(record, supplierGroups);
  }

  return isVendorOnPortal(record, supplierGroups);
}

/**
 * Can we add a new person to this record
 *
 * @param {Object} record search result data object (node from search result tree provider)
 * @returns {boolean} true if a user can be added; false otherwise
 */
export function canAddUserToRecord(record) {
  if (!record || typeof record !== 'object') {
    return false;
  }

  // Can't add a user under a supplier that doesn't exist in eip.
  const canAddUser =
    (record.isSupplier && Array.isArray(record.children) && record.children.length && record.children[0].isPerson) ||
    record.hasPersons ||
    record.eipCompanyKey;

  return !!canAddUser;
}

export function getCompanyKeyFromRecord(record) {
  if (!record || typeof record !== 'object' || Array.isArray(record)) {
    return;
  }

  // if it is a vendor, it will have a company key
  if (record.eipCompanyKey) {
    return record.eipCompanyKey;
  }

  // if we have a person, use their company key
  if (record.isPerson) {
    return record.companyKey;
  }

  // if it is a supplier and has people under it, we use the company key from the first person
  if (!Array.isArray(record.children) || !record.children.length) {
    return undefined;
  }

  const [firstChild] = record.children;
  return firstChild.isPerson ? firstChild.companyKey : undefined;
}
